import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  FiArrowDownLeft,
  FiArrowUpRight,
  FiBookOpen,
  FiDollarSign,
  FiFileText,
  FiFolder,
  FiPieChart,
  FiShoppingBag,
  FiTrendingUp,
  FiTruck,
  FiClipboard,
} from 'react-icons/fi';
import { normalizePagedList } from '../utils/listPagination';
import api from '../config/api';
import { FadeIn } from '../components/motion';
import {
  ErrorState as ErpError,
  errorMessage,
  formatCents as money,
} from '../components/shared/Ui';
import './Services.css';
import '../components/erp/Erp.css';

function sumBuckets(buckets = {}) {
  return Object.values(buckets).reduce((a, b) => a + (Number(b) || 0), 0);
}

const MODULES = [
  {
    to: '/erp/accounting',
    icon: FiDollarSign,
    title: 'Accounting',
    desc: 'General ledger — chart of accounts, journals, payments, and reports. Posting is manual.',
  },
  {
    to: '/erp/selling?tab=documents',
    icon: FiFileText,
    title: 'Sales',
    desc: 'Quotes, invoices, Client POs, credits, and package sales.',
  },
  {
    to: '/orders',
    icon: FiClipboard,
    title: 'Client POs',
    desc: 'Customer engagement orders from requisitions. Manage under Revenue → Client POs.',
  },
  {
    to: '/vendors',
    icon: FiTruck,
    title: 'Vendors',
    desc: 'Supplier master for Vendor POs and bills.',
  },
  {
    to: '/buying',
    icon: FiShoppingBag,
    title: 'Buying',
    desc: 'Vendor quotations, POs, and supplier bills. Distinct from Client POs.',
  },
  {
    to: '/crm?tab=pipeline',
    icon: FiTrendingUp,
    title: 'Sales pipeline',
    desc: 'Leads and opportunities. Candidate touchpoints live under Recruiting → Interactions.',
  },
  {
    to: '/projects',
    icon: FiFolder,
    title: 'Projects',
    desc: 'Delivery projects, budgets, and tasks.',
  },
  {
    to: '/finance/payouts',
    icon: FiDollarSign,
    title: 'Consultant payouts',
    desc: 'Operational pay-run for consultants — not customer revenue or AR.',
  },
];

const StatValue = ({ query, children }) =>
  query.isLoading ? <span className="erp-stat-skeleton" aria-hidden /> : <>{children}</>;

const ErpDashboard = () => {
  const ar = useQuery('erp-ar', async () => (await api.get('/accounting/reports/ar-aging')).data);
  const ap = useQuery('erp-ap', async () => (await api.get('/accounting/reports/ap-aging')).data);
  const drafts = useQuery('erp-je-drafts', async () =>
    normalizePagedList((await api.get('/accounting/journals', { params: { status: 'draft' } })).data)
  );

  const arTotal = sumBuckets(ar.data?.buckets_cents);
  const apTotal = sumBuckets(ap.data?.buckets_cents);
  const draftCount = drafts.data?.pagination?.total ?? 0;
  const loadError = ar.error || ap.error || drafts.error;

  return (
    <div className="erp-page list-page">
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1><FiPieChart aria-hidden /> Finance overview</h1>
          <p className="page-subtitle">
            Books, aging, and shortcuts into Revenue, Procurement, and Delivery — not a separate product.
          </p>
        </div>
      </FadeIn>

      {loadError && <ErpError>{errorMessage(loadError, 'Some finance figures could not be loaded.')}</ErpError>}

      <div className="erp-stat-row">
        <div className="erp-stat erp-stat--receivable">
          <FiArrowDownLeft className="erp-stat-icon" aria-hidden />
          <div className="erp-stat-body">
            <span className="erp-stat-value">
              <StatValue query={ar}>{money(arTotal)}</StatValue>
            </span>
            <span className="erp-stat-label">Open AR</span>
          </div>
        </div>
        <div className="erp-stat erp-stat--payable">
          <FiArrowUpRight className="erp-stat-icon" aria-hidden />
          <div className="erp-stat-body">
            <span className="erp-stat-value">
              <StatValue query={ap}>{money(apTotal)}</StatValue>
            </span>
            <span className="erp-stat-label">Open AP</span>
          </div>
        </div>
        <div className="erp-stat">
          <FiBookOpen className="erp-stat-icon" aria-hidden />
          <div className="erp-stat-body">
            <span className="erp-stat-value">
              <StatValue query={drafts}>{draftCount}</StatValue>
            </span>
            <span className="erp-stat-label">Draft journals</span>
          </div>
        </div>
      </div>

      <section className="erp-panel">
        <div className="erp-panel-head">
          <h2>Workspaces</h2>
          <p className="erp-panel-meta">Jump into ledger, commercial documents, or delivery.</p>
        </div>
        <nav className="erp-link-list" aria-label="Finance workspace links">
          {MODULES.map(({ to, icon: Icon, title, desc }) => (
            <Link key={to} to={to} className="erp-nav-card">
              <Icon aria-hidden />
              <span>
                <span className="erp-nav-card-title">{title}</span>
                <span className="erp-nav-card-desc">{desc}</span>
              </span>
            </Link>
          ))}
        </nav>
      </section>
    </div>
  );
};

export default ErpDashboard;
